import { useState, useEffect } from "react";
import { AlertTriangle, CheckCircle, Shield, Bell } from "lucide-react";
import {
  emailHygieneService,
  EmailMonitoring,
} from "../../services/EmailHygieneService";
import type { EmailAnalysisResult } from "../../services/EmailHygieneService";
import { useToastContext } from "../../context/ToastContext";
import { useLoader } from "../../context/LoaderContext";
import EmailHygieneResults from "./EmailHygieneResults";
import EmailMonitoringDashboard from "./EmailMonitoringDashboard";
import MonitoringStatus from "./MonitoringStatus";

const EmailHygienePage = () => {
  const [domain, setDomain] = useState("");
  const [result, setResult] = useState<EmailAnalysisResult | null>(null);
  const [showMonitoring, setShowMonitoring] = useState(false);
  const [monitorings, setMonitorings] = useState<EmailMonitoring[]>([]);
  const { showToast } = useToastContext();
  const { showLoader, hideLoader } = useLoader();
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  const fetchMonitorings = async () => {
    try {
      const response = await emailHygieneService.getMonitoringList();
      setMonitorings(response.data.items);
    } catch (error) {
      console.error("Failed to fetch monitorings:", error);
    }
  };

  useEffect(() => {
    fetchMonitorings();
  }, []);

  const handleAnalyze = async () => {
    if (!domain.trim()) {
      showToast("error", "Please enter a domain");
      return;
    }

    try {
      setIsAnalyzing(true);
      showLoader();
      const response = await emailHygieneService.analyzeEmail(domain.trim());
      setResult(response.data);
    } catch (error: any) {
      console.error("Error analyzing domain:", error);
      showToast("error", error.message || "Failed to analyze domain");
    } finally {
      setIsAnalyzing(false);
      hideLoader();
    }
  };

  const isMonitored = monitorings.some(
    (m) => m.isActive && m.parameterEmail === domain.trim()
  );

  const hasMonitoringIssues = () => {
    return monitorings.some(
      (monitoring) =>
        monitoring.isActive &&
        monitoring.analysisHistory.length > 0 &&
        monitoring.analysisHistory[monitoring.analysisHistory.length - 1]
          .score < 80
    );
  };

  return (
    <div className="flex flex-col w-full min-h-screen bg-background-default dark:bg-background-dark text-text-primary dark:text-text-primaryDark">
      {showMonitoring ? (
        <EmailMonitoringDashboard
          onClose={() => {
            setShowMonitoring(false);
            fetchMonitorings();
          }}
          monitoringList={monitorings}
          onMonitoringUpdate={fetchMonitorings}
        />
      ) : !result ? (
        <>
          <header className="relative min-h-[70vh] flex items-center justify-center overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-tr from-yellow-DEFAULT/10 via-transparent to-secondary/5" />
            <div className="absolute inset-0 overflow-hidden">
              <div
                className="absolute inset-0 opacity-[0.03] dark:opacity-[0.07]"
                style={{
                  backgroundImage: `
                    radial-gradient(circle at 25px 25px, currentColor 2px, transparent 0),
                    linear-gradient(to right, currentColor 1px, transparent 1px),
                    linear-gradient(to bottom, currentColor 1px, transparent 1px)
                  `,
                  backgroundSize: "50px 50px",
                  maskImage:
                    "radial-gradient(circle at center, black, transparent 80%)",
                }}
              />

              <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-yellow-DEFAULT/10 rounded-full blur-3xl animate-gentle-pulse" />
              <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-secondary/10 rounded-full blur-3xl animate-gentle-pulse delay-1000" />

              <div className="absolute top-1/3 w-full h-px bg-gradient-to-r from-transparent via-yellow-DEFAULT/20 to-transparent" />
              <div className="absolute bottom-1/3 w-full h-px bg-gradient-to-r from-transparent via-secondary/20 to-transparent" />
            </div>

            <div className="absolute top-6 right-6 z-20">
              <MonitoringStatus
                monitorings={monitorings}
                onClick={() => setShowMonitoring(true)}
                hasIssues={hasMonitoringIssues()}
              />
            </div>

            <div className="relative z-10 max-w-4xl mx-auto px-6 py-16">
              <div className="text-center mb-12">
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-secondary/10 text-secondary dark:text-secondary-light text-sm font-medium mb-6">
                  <Shield className="w-4 h-4" />
                  Email Infrastructure Analysis
                </div>
                <h1 className="text-4xl md:text-5xl font-bold mb-6">
                  Monitor Your Email Infrastructure
                </h1>
                <p className="text-lg text-text-secondary dark:text-text-secondaryDark max-w-2xl mx-auto">
                  Get instant insights into your email infrastructure health and
                  security. Choose between a quick analysis or set up continuous
                  monitoring.
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-8 items-start">
                <div className="bg-white dark:bg-background-paperDark rounded-xl p-6 border border-divider dark:border-divider-dark">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-2 rounded-lg bg-yellow-DEFAULT/10">
                      <AlertTriangle className="w-5 h-5 text-yellow-DEFAULT" />
                    </div>
                    <h2 className="text-lg font-semibold">Quick Analysis</h2>
                  </div>
                  <p className="text-text-secondary mb-6">
                    Perform a quick comprehensive check of your domain's email
                    security and health.
                  </p>

                  <div className="space-y-4">
                    <div className="relative">
                      <input
                        type="text"
                        value={domain}
                        onChange={(e) => setDomain(e.target.value)}
                        placeholder="Enter your domain (ie. example.com)"
                        className="w-full px-4 py-3 rounded-lg border border-divider dark:border-divider-dark bg-white dark:bg-background-paperDark focus:border-yellow-DEFAULT focus:ring-1 focus:ring-yellow-DEFAULT/20 transition-all duration-200"
                      />
                    </div>
                    <button
                      onClick={handleAnalyze}
                      disabled={isAnalyzing}
                      className="w-full px-4 py-3 bg-yellow-DEFAULT text-black dark:text-white rounded-lg hover:bg-yellow-DEFAULT/90 transition-colors font-medium relative disabled:opacity-80"
                    >
                      <span
                        className={isAnalyzing ? "opacity-0" : "opacity-100"}
                      >
                        Analyze Now
                      </span>

                      {isAnalyzing && (
                        <div className="absolute inset-0 flex items-center justify-center gap-3">
                          <div className="w-4 h-4 border-2 border-black/20 dark:border-white/20 border-t-black dark:border-t-white rounded-full animate-spin" />
                          <span className="text-sm">
                            Agent Running (10-15s)
                          </span>
                        </div>
                      )}
                    </button>
                    {domain.trim() && (
                      <div
                        className={`flex items-center gap-2 text-sm ${
                          isMonitored
                            ? "text-green-dark"
                            : "text-text-secondary"
                        }`}
                      >
                        {isMonitored ? (
                          <>
                            <Shield className="w-4 h-4" />
                            <span>This domain is already being monitored</span>
                          </>
                        ) : null}
                      </div>
                    )}
                  </div>
                </div>

                <div className="bg-white dark:bg-background-paperDark rounded-xl p-6 border border-divider dark:border-divider-dark">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-2 rounded-lg bg-secondary/10">
                      <Bell className="w-5 h-5 text-secondary dark:text-secondary-light" />
                    </div>
                    <h2 className="text-lg font-semibold">
                      Continuous Monitoring
                    </h2>
                  </div>
                  <p className="text-text-secondary mb-6">
                    Set up automated checks for multiple domains with
                    customizable schedules and notifications.
                  </p>
                  <ul className="space-y-3 mb-6">
                    {[
                      "Monitor multiple domains",
                      "Customizable check frequency",
                      "Email notifications",
                      "Detailed analytics and history",
                    ].map((feature, index) => (
                      <li
                        key={index}
                        className="flex items-center gap-2 text-sm"
                      >
                        <CheckCircle className="w-4 h-4 text-green-dark" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <button
                    onClick={() => setShowMonitoring(true)}
                    className="w-full px-4 py-3 border-2 border-secondary text-secondary dark:text-secondary-light rounded-lg hover:bg-secondary/5 transition-colors font-medium"
                  >
                    Set Up Monitoring
                  </button>
                </div>
              </div>
            </div>
          </header>

          <section className="py-16 px-6 relative overflow-hidden">
            <div className="max-w-7xl mx-auto">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {[
                  {
                    icon: <Shield className="w-6 h-6" />,
                    title: "Security Check",
                    description: "SPF, DKIM, and DMARC verification",
                    gradient: "from-yellow-DEFAULT/20 to-yellow-DEFAULT/5",
                    iconBg: "bg-yellow-DEFAULT/10",
                    iconColor: "text-yellow-DEFAULT",
                  },
                  {
                    icon: <CheckCircle className="w-6 h-6" />,
                    title: "Health Score",
                    description: "Infrastructure health assessment",
                    gradient: "from-secondary/20 to-secondary/5",
                    iconBg: "bg-secondary/10",
                    iconColor: "text-secondary dark:text-secondary-light",
                  },
                  {
                    icon: <AlertTriangle className="w-6 h-6" />,
                    title: "Smart Analysis",
                    description: "AI-powered recommendations",
                    gradient: "from-yellow-DEFAULT/20 to-yellow-DEFAULT/5",
                    iconBg: "bg-yellow-DEFAULT/10",
                    iconColor: "text-yellow-DEFAULT",
                  },
                ].map((feature, index) => (
                  <div
                    key={index}
                    className="group relative p-6 rounded-xl bg-white dark:bg-background-paperDark hover:scale-[1.02] transition-all duration-300"
                  >
                    <div className="absolute inset-0 rounded-xl bg-gradient-to-br opacity-50" />
                    <div className="absolute inset-x-0 -bottom-px h-px bg-gradient-to-r from-transparent via-yellow-DEFAULT/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />

                    <div className="relative flex flex-col">
                      <div
                        className={`w-12 h-12 rounded-xl ${feature.iconBg} ${feature.iconColor} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}
                      >
                        {feature.icon}
                      </div>
                      <h3 className="text-lg font-semibold mb-2">
                        {feature.title}
                      </h3>
                      <p className="text-text-secondary">
                        {feature.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        </>
      ) : (
        <EmailHygieneResults
          result={result}
          onReset={() => setResult(null)}
          onOpenMonitoring={() => setShowMonitoring(true)}
        />
      )}
    </div>
  );
};

export default EmailHygienePage;
